import { tsApiBaseUrl } from '@/config'
import { currentDev } from '@/config/env'
import { showToast } from '@/hooks/useVant'
import { store } from '@/pinia/store'
import { logout } from '@/utils/index.js'
import { MobileAuthService } from '@/tsApi/auth/mobileauth-service'
import { ServiceConfig, RpcService, R, RpcResult, MethodConfig } from 'bt-rpc-base'
import { RpcClient } from 'bt-rpc-web'
import { APP as WyAuth } from './auth/auth-dto.ts'
import { APP as DiscussAuth } from './auth/auth-discuss-dto'
import { APP as Wy } from './wy/wy-dto'
import { APP as Discuss } from './discuss/discuss-dto'
import { APP as Event } from './event/event-tracking-dto.ts'
import { isWhiteListInit } from '@/hooks/useRpcWhiteList'
import CryptoJSCore from 'crypto-js/core'

export type reqType = {
  url: string
  param?: string
}

// 刷新过期时间
export const resetWyExpiresAt = async () => {
  const storeData = store()
  try {
    if (storeData.appData.secret && storeData.appData.session && storeData.headerInfo['c-id'] && storeData.headerInfo['c-meta']) {
      const res = await wyAuthService.refresh(storeData.appData.secret + ':' + storeData.appData.session)
      console.log('刷新过期时间')
      console.log(res)
      if (res.code === 0) {
        localStorage.setItem('expiresAt', res.data.expiresAt.toString())
      }
    }
  } catch (e) {}
}

// 正在进行中的请求队列
const reqSet: Set<string> = new Set()

// 序列化参数
const serializeAndSort = (obj: object) => {
  const sortedKeys = Object.keys(obj).sort()
  const pairs = []

  sortedKeys.forEach((key) => {
    if (obj[key] instanceof Object) {
      pairs.push(`"${key}":${serializeAndSort(obj[key])}`)
    } else {
      pairs.push(`"${key}":"${obj[key]}"`)
    }
  })

  return '{' + pairs.join(',') + '}'
}

// 生成MD5加密key
const createMD5Key = (req: reqType) => {
  const params = req?.param || {}
  const payload = { ...params }
  const key = serializeAndSort(payload)
  return CryptoJSCore.MD5(req.url + key).toString()
}

// 拦截函数
const stopRepeatRequest = (client: RpcClient, req: reqType) => {
  // client - 当前请求信息
  // req - 当前请求信息

  // isWhiteListInit校验当前请求接口是否在白名单里
  const md5Key = createMD5Key(req)
  if (isWhiteListInit(req.url) && reqSet.has(md5Key)) {
    showToast('请求过于频繁，请稍后再试')
    // 调用cancel方法
    client?.constructor?.prototype?.cancel?.()
    return
  }
  reqSet.add(md5Key)
}

// 清除记录
const allowRequest = (req: reqType) => {
  // req - 当前请求信息

  const md5Key = createMD5Key(req)
  if (reqSet.has(md5Key)) {
    reqSet.delete(md5Key)
  }
}

const createRpcClient = (appName = ''): RpcService => {
  const config: ServiceConfig = {
    host: tsApiBaseUrl,
    app: appName,
    withCredentials: true,
    exceptionHandler: (err: any) => {
      console.log('exceptionHandler', err)
      // 登录态过期（code 16）统一在 asyncRequest 中处理（无感刷新 token + 请求级重试），此处不再处理
      if (err.code == 16 || err.code == 705) return
      if (currentDev == 3 || currentDev == 2) {
        if (err.code == 2 || err.code == 3) {
          showToast(err.message || '参数错误')
        } else {
          showToast(err.message || '系统错误')
        }
      }
    },
  }

  const client = RpcClient.create(config)

  const originAsync = client.async.bind(client)
  const asyncRequest = async <DTO>(method: string, param?: any, cfg?: MethodConfig): R<DTO> => {
    const storeData = store()
    // 运行时加载 utils，打断 utils/index.js -> tsApi/service.ts -> utils/index.js 的静态循环依赖
    const { isApp, handleAppTokenExpired, handleWebTokenExpired, handleTokenExpiredFallback } = await import('@/utils')

    const reqInfo: reqType = {
      url: config.app + '/' + method,
      param: param,
    }
    // 阻止重复请求。当上个请求未完成时，相同的请求不会进行
    stopRepeatRequest(config, reqInfo)

    // 单次 RPC 请求：每次调用都读取最新 token，保证 token 刷新后重试携带的是新 token
    const makeRequest = async (): R<DTO> => {
      const cBiz = JSON.stringify({
        cityDistrict: isApp()
          ? `${storeData.appData.cityCode || '320200'},${storeData.appData.districtCode || '320213'}`
          : `${storeData.locationData.cityCode || '320200'},${storeData.locationData.districtCityCode || '320213'}`,
      })

      const payload: any = {
        ...cfg,
        headers: {
          ...cfg?.headers,
          'c-id': storeData.headerInfo['c-id'],
          'c-meta': storeData.headerInfo['c-meta'],
          'c-biz': cBiz,
        },
      }

      if (cfg?.ext?.['c-id']) {
        payload.headers['c-id'] = cfg?.ext?.['c-id']
      }

      if (storeData.appData.accessToken) {
        payload.headers['authorization'] = `Bearer ${storeData.appData.accessToken}`
      }

      const vConsoleRef = window?.VConsole?.instance
      try {
        if (vConsoleRef && currentDev !== 1) {
          const { host, app } = config
          const item = vConsoleRef.network.add({
            url: host + app + method + '[LOG]',
            method: 'POST',
            requestType: 'fetch',
            header: {},
            status: 'Loading',
            postData: param,
            startTime: Date.now(),
          })
          const res = await originAsync(method, param, payload)
          item.endTime = Date.now()
          item.status = 200
          item.response = res
          vConsoleRef.network.update(item.id, item)
          return res
        }
        return await originAsync(method, param, payload)
      } finally {
        // 成功或失败都释放请求记录，确保 Token 刷新及原请求可以正常重试
        allowRequest(reqInfo)
      }
    }

    try {
      return await makeRequest()
    } catch (err: any) {
      // 登录态过期（code 16）：优先无感刷新 token，成功后重试原始请求，失败再执行登录失效兜底
      // 刷新接口自身（MobileAuth/refresh）不参与拦截，避免刷新失败触发 16 时形成无限递归 / 死锁
      if (err?.code === 16) {
        // 刷新接口自身直接抛错，由 doWebRefresh 统一通知订阅者失败，避免重复 toast / goLogin 和递归刷新
        if (method === 'MobileAuth/refresh') throw err

        return await new Promise<RpcResult<DTO>>((resolve, reject) => {
          const retryFn = () => makeRequest().then(resolve).catch(reject)
          const failFn = () => {
            storeData.isRefreshAppInputToken = false
            storeData.appData.accessToken = ''
            handleTokenExpiredFallback(err?.message)
            reject(err)
          }
          // App 环境走原生桥接刷新，非 App 环境（公众号/H5 Web）走 MobileAuthService.refresh
          if (isApp()) {
            handleAppTokenExpired(retryFn, failFn)
          } else {
            handleWebTokenExpired(retryFn, failFn)
          }
        })
      } else if (err?.code == 705) {
        logout()
      }
      throw err
    }
  }
  client.async = asyncRequest
  return client
}

export const wyAuthClient = createRpcClient(WyAuth)
export const discussAuthClient = createRpcClient(DiscussAuth)
export const wyClient = createRpcClient(Wy)
export const discussClient = createRpcClient(Discuss)
export const eventClient = createRpcClient(Event)
export const wyAuthService = new MobileAuthService(wyAuthClient)
export const discussAuthService = new MobileAuthService(discussAuthClient)
